import { Card, CardContent, CircularProgress } from "@mui/material";
import { useState } from "react";
import ArrowLeft from "./components/ArrowLeft";
import ArrowRight from "./components/ArrowRight";
import Button from "./components/Button";
import Check from "./components/Check";
import Close from "./components/Close";
import Dna from "./components/Dna";
import FertilityIcon from "./components/FertilityIcon";
import Heart from "./components/Heart";
import IconButton from "./components/IconButton";
import Pill from "./components/Pill";
import Redo from "./components/Redo";
import Snowflake from "./components/Snowflake";
import Sparkle from "./components/Sparkle";
import Stethoscope from "./components/Stethoscope";
import useWindowDimensions from "./hooks/useWindowDimensions";

const steps = [
  {
    // Intro step
  },
  {
    question: "First, how old are you?",
    options: ["Under 30", "30–34", "35–39", "40+"],
  },
  {
    question: "What’s your situation right now? (choose all that apply)",
    options: [
      "Trying for a while",
      "Just starting to think about it",
      "Had a miscarriage",
      "Diagnosed with fertility issue",
      "Want to freeze my eggs",
      "Considering IVF",
      "Just curious / learning",
    ],
    multi: true,
  },
  {
    question: "How long have you been trying to conceive?",
    options: ["Not trying yet", "< 6 months", "6 months – 1 year", "> 1 year"],
  },
  {
    question: "What matters most to you right now?",
    options: [
      "Getting pregnant faster",
      "Understanding my fertility",
      "Getting a second opinion",
      "Knowing my treatment options",
      "Learning about IVF / Egg freezing",
    ],
  },
];

export default function FertilityQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const { _, width } = useWindowDimensions();

  const handlePrev = () => {
    const current = steps[step];
    const key = current.question;
    setAnswers({ ...answers, [key]: current.multi ? [] : "" });
    setStep((prev) => prev - 1);
  };

  const handleNext = () => {
    setStep((prev) => prev + 1);
  };

  const FractalGlass = () => (
    <div className="fractal">
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
      <div className="fractal-piece"></div>
    </div>
  );

  const handleOptionSelect = (option) => {
    const current = steps[step];
    const key = current.question;
    if (current.multi) {
      const prev = answers[key] || [];
      const exists = prev.includes(option);
      const updated = exists
        ? prev.filter((o) => o !== option)
        : [...prev, option];
      setAnswers({ ...answers, [key]: updated });
    } else {
      setAnswers({ ...answers, [key]: option });
    }
  };

  if (step === 0) {
    if (width <= 700)
      return (
        <div className="mobile-container mobile-body-text">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignSelf: "stretch",
              gap: "12px",
            }}
          >
            <div className="mobile-header mobile-body-text small-letter-spacing">
              Your Personalized Fertility Plan
            </div>
            <div className="mobile-title-section">
              <span className="mobile-huge-text">
                FAST.
                <br />
                PRIVATE.
                <br />
                MADE
                <br />
                WITH
                <br />
                DOCTORS.
              </span>
              <FertilityIcon size="100%" />
            </div>
          </div>
          <span
            className="small-letter-spacing"
            style={{ alignSelf: "stretch", paddingInline: "8px" }}
          >
            Answer 4 simple questions and get all the answers you need to begin
            your fertility journey right now.
          </span>
          <div
            className="inner-card mobile-body-text small-letter-spacing"
            style={{
              background:
                "linear-gradient(0deg, rgba(160, 214, 222, 0.20) 0%, rgba(160, 214, 222, 0.20) 100%), url(" +
                require("./images/clinic.png") +
                ") lightgray 50% / cover no-repeat",
            }}
          >
            <div
              className="inner-card"
              style={{
                padding: "20px",
                background: "rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(15.399999618530273px)",
                textAlign: "center",
              }}
            >
              <IconButton
                icon={<ArrowRight color="#F9FFFE" />}
                sx={{ position: "absolute", right: "12px", top: "12px" }}
                onClick={handleNext}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleNext();
                }}
              />
              Get matched with state-of-the-art clinics and specialists that can
              tackle your problems head-on.
            </div>
          </div>
          <Button
            fullWidth
            variant="primary"
            label="Start Quiz"
            rightIcon={<ArrowRight />}
            onClick={handleNext}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleNext();
            }}
          />
        </div>
      );
    return (
      <Card className="card">
        <FractalGlass />
        <CardContent className="card-content">
          <div className="header heading-text">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FertilityIcon />
              YOUR PERSONALIZED FERTILITY PLAN
            </div>
            <Button
              size="S"
              label="START HERE"
              onClick={handleNext}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleNext();
              }}
            />
          </div>
          <div
            className="body-text small-letter-spacing"
            style={{
              alignSelf: "flex-end",
              width: "486px",
            }}
          >
            Take this personalized quiz and learn all you need to know about
            your fertility needs. Get to know your body in under 60 seconds.
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              alignSelf: "stretch",
            }}
          >
            <div className="huge-text">
              FAST<span style={{ fontSize: "36px" }}>.</span> PRIVATE
              <span style={{ fontSize: "36px" }}>.</span>
              <br />
              MADE WITH DOCTORS<span style={{ fontSize: "36px" }}>.</span>
            </div>
            <Button
              variant="tertiary"
              label="TAKE THE QUIZ"
              rightIcon={<ArrowRight />}
              onClick={handleNext}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleNext();
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              gap: "20px",
              alignSelf: "stretch",
              flex: "1 0 0",
            }}
          >
            <div className="inner-card body-text small-letter-spacing">
              <div
                style={{ width: "100%", height: "1px", background: "#053137" }}
              ></div>
              Answer 4 simple questions and get all the answers you need to
              begin your fertility journey right now.
              <div
                style={{ width: "100%", height: "1px", background: "#053137" }}
              ></div>
            </div>
            <div
              className="inner-card"
              style={{
                background:
                  "linear-gradient(0deg, rgba(160, 214, 222, 0.20) 0%, rgba(160, 214, 222, 0.20) 100%), url(" +
                  require("./images/pregnant-woman.jpg") +
                  ") lightgray -104.817px 0px / cover no-repeat",
              }}
            ></div>
            <div
              className="inner-card body-text small-letter-spacing"
              style={{
                background:
                  "linear-gradient(0deg, rgba(160, 214, 222, 0.20) 0%, rgba(160, 214, 222, 0.20) 100%), url(" +
                  require("./images/clinic.png") +
                  ") lightgray 50% / cover no-repeat",
              }}
            >
              <div
                className="inner-card"
                style={{
                  padding: "20px",
                  background: "rgba(255, 255, 255, 0.3)",
                  backdropFilter: "blur(15.399999618530273px)",
                  textAlign: "center",
                }}
              >
                <IconButton
                  icon={<ArrowRight color="#F9FFFE" />}
                  sx={{ position: "absolute", right: "12px", top: "12px" }}
                  onClick={handleNext}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleNext();
                  }}
                />
                Get matched with state-of-the-art clinics and specialists that
                can tackle your problems head-on.
              </div>
            </div>
          </div>
          <div
            className="body-text small-letter-spacing"
            style={{
              alignSelf: "flex-start",
              width: "524px",
            }}
          >
            There’s more to fertility than age. Take a minute to uncover key
            factors that could be affecting your body—and what to do about them.
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 5) {
    setTimeout(() => setStep(6), 3000);
    if (width <= 700)
      return (
        <div className="mobile-container mobile-body-text">
          <div className="mobile-header mobile-body-text small-letter-spacing">
            Your Personalized Fertility Plan
            <IconButton
              icon={<Close />}
              sx={{
                background: "rgba(5, 49, 55, 0.10)",
                border: "1px solid rgba(5, 49, 55, 0.20)",
              }}
              onClick={() => {
                setAnswers({});
                setStep(0);
              }}
            />
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress"
                style={{ width: (step / steps.length) * 100 + "%" }}
              ></div>
            </div>
            <span style={{ width: "7vw" }}>
              {step}/{steps.length}
            </span>
          </div>
          <div className="loading-container">
            <CircularProgress color="#053137" />
            Matching you with a specialist...
          </div>
        </div>
      );
    return (
      <Card className="card">
        <FractalGlass />
        <CardContent className="card-content">
          <div className="header">
            <div
              className="heading-text"
              style={{ display: "flex", alignItems: "center", gap: "12px" }}
            >
              <FertilityIcon />
              YOUR PERSONALIZED FERTILITY PLAN
            </div>
            <div className="body-text" style={{ marginRight: "8px" }}>
              Step {step}/{steps.length}
            </div>
          </div>
          <div className="inner-card body-text">
            <div className="loading-container">
              <CircularProgress color="#053137" />
              Matching you with a specialist...
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step > steps.length) {
    if (width <= 700)
      return (
        <div
          className="mobile-container mobile-body-text small-letter-spacing"
          style={{ gap: "12px", justifyContent: "space-between" }}
        >
          <div className="mobile-header">
            Your Fertility Expert Match
            <IconButton
              icon={<Close />}
              sx={{
                background: "rgba(5, 49, 55, 0.10)",
                border: "1px solid rgba(5, 49, 55, 0.20)",
              }}
              onClick={() => {
                setAnswers({});
                setStep(0);
              }}
            />
          </div>
          Based on your answers, we've matched you with a leading fertility
          specialist experienced in the services you may need.
          <div className="doctor-container-mobile">
            <img
              src={require("./images/Stefanos-Chandakas.png")}
              style={{
                width: "35%",
                borderRadius: "8px",
                flexShrink: 0,
                objectFit: "cover",
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              <span className="mobile-section-title">
                Dr Stefanos Chandakas
              </span>
              Fertility Specialist at HYGEIA IVF Embryogenesis Unit
              <ul style={{ margin: 0, paddingInlineStart: "20px" }}>
                <li style={{ marginBottom: "6px" }}>
                  Over 20 years specializing in fertility care
                </li>
                <li>
                  Expertise across: IVF, Egg Freezing, AI-Assist IVF, IVF with
                  ICSI, Egg donation, Ovarian Rejuvenation
                </li>
              </ul>
            </div>
          </div>
          European leader in personalized fertility solutions for low ovarian
          reserve, IVF over 40, and complex cases.
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignSelf: "stretch",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignSelf: "stretch",
              }}
            >
              <span className="mobile-section-title">
                Recommended Services for You
              </span>
              Services selected based on your quiz profile
            </div>
            <div className="services-table-mobile">
              <div className="service-item-mobile">
                <Stethoscope size={20} />
                Fertility Consultation
              </div>
              <div className="service-item-mobile">
                <Dna size={20} />
                IVF and IVF-ICSI
              </div>
              <div className="service-item-mobile">
                <Pill size={20} />
                Mini IVF (minimal medication)
              </div>
              <div className="service-item-mobile">
                <Snowflake size={20} />
                Egg Freezing and Donation Programs
              </div>
              <div
                className="service-item-mobile"
                style={{ borderBottom: "none" }}
              >
                <Heart size={20} />
                Fertility Support up to Age 54
              </div>
            </div>
          </div>
          <div className="buttons-container-column">
            <Button
              leftIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12.5556 11.7689V7C12.5556 6.84222 12.5022 6.71037 12.3956 6.60444C12.2889 6.49852 12.1567 6.44518 11.9989 6.44444C11.8411 6.4437 11.7093 6.49704 11.6033 6.60444C11.4974 6.71185 11.4444 6.8437 11.4444 7V11.8656C11.4444 11.9789 11.4652 12.0889 11.5067 12.1956C11.5481 12.3015 11.617 12.403 11.7133 12.5L15.6578 16.4444C15.7615 16.5481 15.8893 16.6037 16.0411 16.6111C16.193 16.6185 16.3274 16.563 16.4444 16.4444C16.5615 16.3259 16.6207 16.1948 16.6222 16.0511C16.6237 15.9074 16.5644 15.7763 16.4444 15.6578L12.5556 11.7689ZM12.0033 22C10.6211 22 9.32111 21.7378 8.10333 21.2133C6.8863 20.6881 5.82741 19.9756 4.92667 19.0756C4.02593 18.1756 3.31296 17.1178 2.78778 15.9022C2.26259 14.6867 2 13.387 2 12.0033C2 10.6196 2.26259 9.31963 2.78778 8.10333C3.31222 6.8863 4.0237 5.82741 4.92222 4.92667C5.82074 4.02593 6.87889 3.31296 8.09667 2.78778C9.31445 2.26259 10.6144 2 11.9967 2C13.3789 2 14.6789 2.26259 15.8967 2.78778C17.1137 3.31222 18.1726 4.02407 19.0733 4.92333C19.9741 5.82259 20.687 6.88074 21.2122 8.09778C21.7374 9.31481 22 10.6144 22 11.9967C22 13.3789 21.7378 14.6789 21.2133 15.8967C20.6889 17.1144 19.9763 18.1733 19.0756 19.0733C18.1748 19.9733 17.117 20.6863 15.9022 21.2122C14.6874 21.7381 13.3878 22.0007 12.0033 22ZM12 20.8889C14.463 20.8889 16.5604 20.0233 18.2922 18.2922C20.0241 16.5611 20.8896 14.4637 20.8889 12C20.8881 9.5363 20.0226 7.43889 18.2922 5.70778C16.5619 3.97667 14.4644 3.11111 12 3.11111C9.53556 3.11111 7.43815 3.97704 5.70778 5.70889C3.97741 7.44074 3.11185 9.53778 3.11111 12C3.11037 14.4622 3.9763 16.5596 5.70889 18.2922C7.44148 20.0248 9.53852 20.8904 12 20.8889Z"
                    fill="#F9FFFE"
                  />
                </svg>
              }
              label="Schedule your next consultation"
              onClick={() =>
                window.open(
                  "https://calendly.com/drchandakas/rantevou?embed_domain=www.drchandakas.gr&embed_type=Inline&hide_landing_page_details=1&hide_gdpr_banner=1&back=1"
                )
              }
              fullWidth
            />
            <Button
              variant="secondary"
              leftIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M12.5 22C6.977 22 2.5 17.523 2.5 12C2.5 6.477 6.977 2 12.5 2C18.023 2 22.5 6.477 22.5 12C22.5 17.523 18.023 22 12.5 22ZM12.5 20.8C14.8339 20.8 17.0722 19.8729 18.7225 18.2225C20.3729 16.5722 21.3 14.3339 21.3 12C21.3 9.66609 20.3729 7.42778 18.7225 5.77746C17.0722 4.12714 14.8339 3.2 12.5 3.2C10.1661 3.2 7.92778 4.12714 6.27746 5.77746C4.62714 7.42778 3.7 9.66609 3.7 12C3.7 14.3339 4.62714 16.5722 6.27746 18.2225C7.92778 19.8729 10.1661 20.8 12.5 20.8ZM11.9 10H13.1V17H11.9V10ZM12.5 9C12.2348 9 11.9804 8.89464 11.7929 8.70711C11.6054 8.51957 11.5 8.26522 11.5 8C11.5 7.73478 11.6054 7.48043 11.7929 7.29289C11.9804 7.10536 12.2348 7 12.5 7C12.7652 7 13.0196 7.10536 13.2071 7.29289C13.3946 7.48043 13.5 7.73478 13.5 8C13.5 8.26522 13.3946 8.51957 13.2071 8.70711C13.0196 8.89464 12.7652 9 12.5 9Z"
                    fill="#053137"
                  />
                </svg>
              }
              label="Learn more about Dr Chandakas"
              onClick={() =>
                window.open(
                  "https://www.drchandakas.gr/i-omada-mas/dr-stefanos-xandakas-md-mba-phd"
                )
              }
              fullWidth
            />
          </div>
        </div>
      );
    return (
      <Card className="card">
        <FractalGlass />
        <CardContent className="card-content" style={{ gap: "20px" }}>
          <div className="header">
            <div
              className="heading-text"
              style={{ display: "flex", alignItems: "center", gap: "12px" }}
            >
              <FertilityIcon />
              YOUR FERTILITY EXPERT MATCH
            </div>
            <Button
              size="S"
              label="RETAKE QUIZ"
              onClick={() => {
                setAnswers({});
                setStep(1);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setAnswers({});
                  setStep(1);
                }
              }}
            />
          </div>
          <div className="cards-container">
            <div
              className="inner-card body-text"
              style={{
                padding: "24px",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              <span style={{ paddingInline: "8px" }}>
                Based on your answers, we've matched you with a leading
                fertility specialist experienced in the services you may need.
              </span>
              <div className="doctor-container">
                <img
                  src={require("./images/Stefanos-Chandakas.png")}
                  style={{
                    width: "40%",
                    borderRadius: "8px",
                    flexShrink: 0,
                    objectFit: "cover",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontWeight: 700 }}>Dr Stefanos Chandakas</span>
                  Fertility Specialist at HYGEIA IVF Embryogenesis Unit
                  <ul style={{ margin: 0, paddingInlineStart: "20px" }}>
                    <li style={{ marginBottom: "6px" }}>
                      Over 20 years specializing in fertility care
                    </li>
                    <li>
                      Expertise across: IVF, Egg Freezing, AI-Assist IVF, IVF
                      with ICSI, Egg donation, Ovarian Rejuvenation
                    </li>
                  </ul>
                </div>
              </div>
              <span style={{ paddingInline: "8px" }}>
                European leader in personalized fertility solutions for low
                ovarian reserve, IVF over 40, and complex cases.
              </span>
              <span style={{ paddingInline: "8px" }}>
                “
                <span style={{ fontStyle: "italic" }}>
                  Always informative, helpful and explaining everything needed.
                  His calmness and the wonderful team he has contribute to the
                  best possible result.
                </span>
                ” – Patient
              </span>
              <Button
                variant="secondary"
                leftIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M12.5 22C6.977 22 2.5 17.523 2.5 12C2.5 6.477 6.977 2 12.5 2C18.023 2 22.5 6.477 22.5 12C22.5 17.523 18.023 22 12.5 22ZM12.5 20.8C14.8339 20.8 17.0722 19.8729 18.7225 18.2225C20.3729 16.5722 21.3 14.3339 21.3 12C21.3 9.66609 20.3729 7.42778 18.7225 5.77746C17.0722 4.12714 14.8339 3.2 12.5 3.2C10.1661 3.2 7.92778 4.12714 6.27746 5.77746C4.62714 7.42778 3.7 9.66609 3.7 12C3.7 14.3339 4.62714 16.5722 6.27746 18.2225C7.92778 19.8729 10.1661 20.8 12.5 20.8ZM11.9 10H13.1V17H11.9V10ZM12.5 9C12.2348 9 11.9804 8.89464 11.7929 8.70711C11.6054 8.51957 11.5 8.26522 11.5 8C11.5 7.73478 11.6054 7.48043 11.7929 7.29289C11.9804 7.10536 12.2348 7 12.5 7C12.7652 7 13.0196 7.10536 13.2071 7.29289C13.3946 7.48043 13.5 7.73478 13.5 8C13.5 8.26522 13.3946 8.51957 13.2071 8.70711C13.0196 8.89464 12.7652 9 12.5 9Z"
                      fill="#053137"
                    />
                  </svg>
                }
                label="Learn more about Dr Chandakas"
                onClick={() =>
                  window.open(
                    "https://www.drchandakas.gr/i-omada-mas/dr-stefanos-xandakas-md-mba-phd"
                  )
                }
                fullWidth
              />
            </div>
            <div
              className="inner-card body-text"
              style={{
                padding: "24px",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  alignSelf: "stretch",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    paddingInline: "8px",
                    flexDirection: "column",
                    alignSelf: "stretch",
                  }}
                >
                  <span style={{ fontWeight: 700 }}>
                    Recommended Services for You
                  </span>
                  Services selected based on your quiz profile
                </div>
                <div className="services-table">
                  <div className="service-item">
                    <Stethoscope />
                    Fertility Consultation
                  </div>
                  <div className="service-item">
                    <Dna />
                    IVF and IVF-ICSI
                  </div>
                  <div className="service-item">
                    <Pill />
                    Mini IVF (minimal medication)
                  </div>
                  <div className="service-item">
                    <Sparkle />
                    AI-assisted IVF
                  </div>
                  <div className="service-item">
                    <Snowflake />
                    Egg Freezing and Donation Programs
                  </div>
                  <div className="service-item">
                    <Redo />
                    PRP Therapy for Ovarian Rejuvenation
                  </div>
                  <div className="service-item">
                    <Heart />
                    Fertility Support up to Age 54
                  </div>
                </div>
              </div>
              <Button
                leftIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12.5556 11.7689V7C12.5556 6.84222 12.5022 6.71037 12.3956 6.60444C12.2889 6.49852 12.1567 6.44518 11.9989 6.44444C11.8411 6.4437 11.7093 6.49704 11.6033 6.60444C11.4974 6.71185 11.4444 6.8437 11.4444 7V11.8656C11.4444 11.9789 11.4652 12.0889 11.5067 12.1956C11.5481 12.3015 11.617 12.403 11.7133 12.5L15.6578 16.4444C15.7615 16.5481 15.8893 16.6037 16.0411 16.6111C16.193 16.6185 16.3274 16.563 16.4444 16.4444C16.5615 16.3259 16.6207 16.1948 16.6222 16.0511C16.6237 15.9074 16.5644 15.7763 16.4444 15.6578L12.5556 11.7689ZM12.0033 22C10.6211 22 9.32111 21.7378 8.10333 21.2133C6.8863 20.6881 5.82741 19.9756 4.92667 19.0756C4.02593 18.1756 3.31296 17.1178 2.78778 15.9022C2.26259 14.6867 2 13.387 2 12.0033C2 10.6196 2.26259 9.31963 2.78778 8.10333C3.31222 6.8863 4.0237 5.82741 4.92222 4.92667C5.82074 4.02593 6.87889 3.31296 8.09667 2.78778C9.31445 2.26259 10.6144 2 11.9967 2C13.3789 2 14.6789 2.26259 15.8967 2.78778C17.1137 3.31222 18.1726 4.02407 19.0733 4.92333C19.9741 5.82259 20.687 6.88074 21.2122 8.09778C21.7374 9.31481 22 10.6144 22 11.9967C22 13.3789 21.7378 14.6789 21.2133 15.8967C20.6889 17.1144 19.9763 18.1733 19.0756 19.0733C18.1748 19.9733 17.117 20.6863 15.9022 21.2122C14.6874 21.7381 13.3878 22.0007 12.0033 22ZM12 20.8889C14.463 20.8889 16.5604 20.0233 18.2922 18.2922C20.0241 16.5611 20.8896 14.4637 20.8889 12C20.8881 9.5363 20.0226 7.43889 18.2922 5.70778C16.5619 3.97667 14.4644 3.11111 12 3.11111C9.53556 3.11111 7.43815 3.97704 5.70778 5.70889C3.97741 7.44074 3.11185 9.53778 3.11111 12C3.11037 14.4622 3.9763 16.5596 5.70889 18.2922C7.44148 20.0248 9.53852 20.8904 12 20.8889Z"
                      fill="#F9FFFE"
                    />
                  </svg>
                }
                label="Schedule your next consultation"
                onClick={() =>
                  window.open(
                    "hhttps://calendly.com/drchandakas/rantevou?embed_domain=www.drchandakas.gr&embed_type=Inline&hide_landing_page_details=1&hide_gdpr_banner=1&back=1"
                  )
                }
                fullWidth
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const current = steps[step];
  const selected = answers[current.question] || (current.multi ? [] : "");

  if (width <= 700)
    return (
      <div className="mobile-container mobile-body-text">
        <div className="mobile-header small-letter-spacing">
          Your Personalized Fertility Plan
          <IconButton
            icon={<Close />}
            sx={{
              background: "rgba(5, 49, 55, 0.10)",
              border: "1px solid rgba(5, 49, 55, 0.20)",
            }}
            onClick={() => {
              setAnswers({});
              setStep(0);
            }}
          />
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div
              className="progress"
              style={{ width: (step / steps.length) * 100 + "%" }}
            ></div>
          </div>
          <span style={{ width: "7vw" }}>
            {step}/{steps.length}
          </span>
        </div>
        <div className="question-container">{current.question}</div>
        <div className="answers-container">
          {current.options.map((option) => (
            <Button
              key={option}
              leftIcon={current.multi ? <Check /> : null}
              variant={
                current.multi
                  ? selected.includes(option)
                    ? "primary"
                    : "secondary"
                  : selected === option
                  ? "primary"
                  : "secondary"
              }
              fullWidth
              label={option}
              onClick={() => handleOptionSelect(option)}
              sx={{ justifyContent: "flex-start", paddingInline: "16px" }}
            />
          ))}
        </div>
        <div className="buttons-container">
          <Button
            variant="secondary"
            leftIcon={<ArrowLeft />}
            label={"Back"}
            onClick={handlePrev}
            fullWidth
          />
          <Button
            rightIcon={<ArrowRight color="#F9FFFE" />}
            label={"Next"}
            onClick={handleNext}
            disabled={current.multi ? selected.length === 0 : !selected}
            fullWidth
          />
        </div>
      </div>
    );
  return (
    <Card className="card">
      <FractalGlass />
      <CardContent className="card-content">
        <div className="header">
          <div
            className="heading-text"
            style={{ display: "flex", alignItems: "center", gap: "12px" }}
          >
            <FertilityIcon />
            YOUR PERSONALIZED FERTILITY PLAN
          </div>
          <div className="body-text" style={{ marginRight: "8px" }}>
            Step {step}/{steps.length}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "20px",
            alignSelf: "stretch",
            flex: "1 0 0",
          }}
        >
          <div
            className="inner-card"
            style={{
              background:
                "linear-gradient(0deg, rgba(160, 214, 222, 0.2) 0%, rgba(160, 214, 222, 0.2) 100%), url(" +
                require("./images/step" + step + ".jpg") +
                ") lightgray 50% / cover no-repeat",
            }}
          ></div>
          <div className="inner-card body-text" style={{ paddingTop: "40px" }}>
            <span style={{ paddingInline: "8px" }}>{current.question}</span>
            <div
              style={{ width: "100%", height: "1px", background: "#053137" }}
            ></div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "12px",
                flex: "1 0 0",
                alignSelf: "stretch",
              }}
            >
              {current.options.map((option) => (
                <Button
                  key={option}
                  leftIcon={
                    current.multi ? (
                      <Check
                        color={
                          selected.includes(option) ? "#F9FFFE" : "#053137"
                        }
                      />
                    ) : null
                  }
                  variant={
                    current.multi
                      ? selected.includes(option)
                        ? "primary"
                        : "secondary"
                      : selected === option
                      ? "primary"
                      : "secondary"
                  }
                  fullWidth
                  label={option}
                  onClick={() => handleOptionSelect(option)}
                  sx={{ justifyContent: "flex-start", paddingInline: "16px" }}
                />
              ))}
            </div>
            <div style={{ display: "flex", gap: "12px", alignSelf: "stretch" }}>
              <Button
                variant="secondary"
                leftIcon={<ArrowLeft />}
                label={"Back"}
                onClick={handlePrev}
                fullWidth
              />
              <Button
                rightIcon={<ArrowRight color="#F9FFFE" />}
                label={"Next"}
                onClick={handleNext}
                disabled={current.multi ? selected.length === 0 : !selected}
                fullWidth
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
